import React from "react";
import * as ReactHighcharts from "react-highcharts";
import { MonthInMs, QuarterInMs, YearInMs } from "./model";
import { canReadCosts, canReadSales } from "utilities/documentUtils";

interface DocumentChartProps {
  period: server.dto.PeriodOptions
  documentType: server.dto.DocumentTypes
  documentTotals: server.dto.GroupedDocumentTotals[]
  currency: string
}

export const DocumentChart = (props: DocumentChartProps) => {
  return <SalesChart
    period={props.period}
    series={getSeriesForDocumentTotals(props.documentType, props.documentTotals)}
    currency={props.currency}
  />;
}

interface InvoiceChartProps {
  period: server.dto.PeriodOptions
  invoiceTotals: server.dto.GroupedInvoiceTotals[]
  currency: string
}

export const InvoiceChart = (props: InvoiceChartProps) => {
  return <SalesChart
    period={props.period}
    series={getSeriesForInvoiceTotals(props.invoiceTotals)}
    currency={props.currency}
  />;
}

interface SalesChartProps {
  period: server.dto.PeriodOptions
  series: any
  currency: string
}

const SalesChart = (props: SalesChartProps) => {
  const getTickInterval = (): number => {
    switch (props.period) {
      case "year":
        return YearInMs;
      case "quarter":
        return QuarterInMs;
      case "month":
        return MonthInMs;
    }
  };

  const getChartConfig = () => {
    const currency: string = props.currency.toUpperCase();

    return {
      chart: {
        style: {
          fontFamily: "inherit"
        },
        type: "column",
        backgroundColor: "transparent"
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderColor: "transparent",
          dataLabels: {
            style: {
              color: "var(--text-color-light)"
            }
          }
        },
      },
      legend: {
        itemStyle: {
          color: "var(--text-color-light)",
        },
        itemHoverStyle: {
          color: "var(--text-color)",
        },
        itemHiddenStyle: {
          color: "var(--text-color-lighter)",
        },
      },
      series: props.series,
      title: {
        text: ""
      },
      tooltip: {
        pointFormat: `<p>{series.name}<p><br/><p><b>${currency} {point.y:,.0f}</b></p>`
      },
      xAxis: {
        labels: {
          style: {
            color: "var(--text-color-light)"
          }
        },
        tickInterval: getTickInterval(),
        type: "datetime"
      },
      yAxis: {
        labels: {
          style: {
            color: "var(--text-color-light)"
          }
        },
        title: {
          style: {
            color: "var(--text-color-light)"
          },
          enabled: true,
          rotation: 0,
          text: currency
        }
      },
      credits: {
        enabled: false
      }
    };
  };

  return <div>
    <ReactHighcharts config={getChartConfig()} />
  </div>;
}

const getSeriesForDocumentTotals = (documentType: server.dto.DocumentTypes, documentTotals: server.dto.GroupedDocumentTotals[]) => {
  // Simple number/date sorting (ascending) that works for our series data structure
  const ascDateSort = (a, b) => a[0] - +b[0];

  let totalCostsExTax = documentTotals.map((data) => [new Date(data.dateAggregate).getTime(), data.totalCostExcludingTax]);
  let totalSalesExTax = documentTotals.map((data) => [new Date(data.dateAggregate).getTime(), data.totalSalesExcludingTax]);

  totalCostsExTax = totalCostsExTax.sort(ascDateSort);
  totalSalesExTax = totalSalesExTax.sort(ascDateSort);

  const chart = [];

  if (canReadCosts(documentType)) {
    chart.push({
      data: totalCostsExTax,
      name: RESX.Financial.TotalCostsExTax,
      color: "var(--purple)",
      stack: "first"
    });
  }

  if (canReadSales(documentType)) {
    chart.push({
      data: totalSalesExTax,
      name: RESX.Financial.TotalSalesExTax,
      color: "var(--green)",
      stack: "second"
    });
  }

  return chart;
}

const getSeriesForInvoiceTotals = (documentTotals: server.dto.GroupedInvoiceTotals[]) => {
  // Simple number/date sorting (ascending) that works for our series data structure
  const ascDateSort = (a, b) => a[0] - +b[0];

  let outstandingSeriesData = documentTotals.map((data) => [new Date(data.dateAggregate).getTime(), data.totalSalesIncludingTax - data.totalSettled]);
  let settledSeriesData = documentTotals.map((data) => [new Date(data.dateAggregate).getTime(), data.totalSettled]);
  let totalSalesExTax = documentTotals.map((data) => [new Date(data.dateAggregate).getTime(), data.totalSalesExcludingTax]);

  outstandingSeriesData = outstandingSeriesData.sort(ascDateSort);
  settledSeriesData = settledSeriesData.sort(ascDateSort);
  totalSalesExTax = totalSalesExTax.sort(ascDateSort);

  return [
    {
      data: outstandingSeriesData,
      name: RESX.Financial.Outstanding,
      color: "var(--pink)",
      stack: "first"
    }, {
      data: settledSeriesData,
      name: RESX.Financial.Settled,
      color: "var(--blue)",
      stack: "first"
    }, {
      data: totalSalesExTax,
      name: RESX.Financial.TotalSalesExTax,
      color: "var(--green)",
      stack: "second"
    }];
};